import * as __req_0 from "../../../assets/images/MB_logo.png";
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import mqttService from "@monitor/mqttService";
import NOTIFICATION_TYPES from "@common/constants/notificationTypes";
import Push from "push.js";
import * as userActions from "@profile/actions/userActions";
import { store } from "../../../store";
const MBIcon = (__req_0.default !== undefined ? __req_0.default : __req_0);

export const requestPermission = async () => {
  // If OneSignal SDK is loaded and active, opt in through OneSignal
  if (window.OneSignal?.User?.PushSubscription) {
    try {
      await window.OneSignal.User.PushSubscription.optIn();
      return;
    } catch (err) {
      console.error(
        "OneSignal optIn failed, falling back to native Notification:",
        err,
      );
    }
  }

  // Push to OneSignalDeferred in case OneSignal loads slightly later
  if (window.OneSignalDeferred && Array.isArray(window.OneSignalDeferred)) {
    window.OneSignalDeferred.push(async (OneSignal) => {
      try {
        await OneSignal.User.PushSubscription.optIn();
      } catch (err) {
        console.error("Deferred OneSignal optIn failed:", err);
      }
    });
  }

  // Fallback to browser native Notification API
  if (typeof Notification === "undefined") {
    alert("Notifications are not supported by this browser.");
    return;
  }

  if (Notification.permission === "denied") {
    alert(
      "Notifications are currently blocked for this site.\n\nTo enable them:\n1. Click the Site Settings / Tune / Padlock icon in your browser address bar next to the URL\n2. Change 'Notifications' to 'Allow'\n3. Refresh the page and click the bell again.",
    );
    store.dispatch(userActions.setNotificationPermission(false));
    return;
  }

  try {
    let permission = Notification.permission;
    if (permission !== "granted") {
      // Support both Promise and callback-based requestPermission
      const requestPromise = Notification.requestPermission();
      if (requestPromise && typeof requestPromise.then === "function") {
        permission = await requestPromise;
      } else {
        permission = await new Promise((resolve) => {
          Notification.requestPermission((result) => resolve(result));
        });
      }
    }

    if (permission === "granted") {
      store.dispatch(userActions.setNotificationPermission(true));
      try {
        Push.create("MiniBrew Brewery Portal", {
          body: "Notifications are now enabled!",
          icon: MBIcon,
          timeout: 5000,
          onClick: function () {
            window.focus();
            this.close();
          },
        });
      } catch (pushErr) {
        if (typeof Notification !== "undefined") {
          new Notification("MiniBrew Brewery Portal", {
            body: "Notifications are now enabled!",
          });
        }
      }
    } else {
      store.dispatch(userActions.setNotificationPermission(false));
    }
  } catch (err) {
    console.error("Failed to request native notification permission:", err);
  }
};

export const isIOS = () => {
  return (
    ["iPad", "iPhone", "iPod"].includes(navigator.platform) ||
    // iPad on iOS 13 detection
    (navigator.userAgent.includes("Mac") && "ontouchend" in document)
  );
};

const notifyUser = (notification) => {
  // If user is actively looking at the application, suppress OS-level browser notification
  // to avoid redundant popups while they are already viewing the UI / carousel.
  if (
    typeof document !== "undefined" &&
    document.visibilityState === "visible"
  ) {
    return;
  }
  if (notification.message) {
    Push.create("Minibrew Brewery Portal", {
      requireInteraction: true,
      body: notification.message,
      icon: MBIcon,
      onClick: function () {
        window.focus();
        this.close();
      },
    });
  }
};

const GlobalNotificationDisplayer = () => {
  const [lastNotification, setLastNotification] = useState([]);
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.userInfo);

  const lastNotificationRef = useRef(lastNotification);
  const userInfoRef = useRef(userInfo);

  useEffect(() => {
    lastNotificationRef.current = lastNotification;
  }, [lastNotification]);

  useEffect(() => {
    userInfoRef.current = userInfo;
  }, [userInfo]);

  useEffect(() => {
    let pushSub = null;
    const handleChange = (event) => {
      const currentOptedIn = event?.current?.optedIn || false;
      dispatch(userActions.setNotificationPermission(currentOptedIn));
    };

    if (window.OneSignalDeferred) {
      window.OneSignalDeferred.push((OneSignal) => {
        pushSub = OneSignal?.User?.PushSubscription;
        const isOptedIn = pushSub?.optedIn || false;
        dispatch(userActions.setNotificationPermission(isOptedIn));

        pushSub?.addEventListener("change", handleChange);

        OneSignal.Notifications.addEventListener(
          "foregroundWillDisplay",
          (event) => {
            if (
              typeof document !== "undefined" &&
              document.visibilityState === "visible"
            ) {
              event.preventDefault();
            }
          },
        );
      });
    } else if (
      typeof Notification !== "undefined" &&
      Notification.permission === "granted"
    ) {
      dispatch(userActions.setNotificationPermission(true));
    }

    return () => {
      pushSub?.removeEventListener("change", handleChange);
    };
  }, [dispatch]);

  useEffect(() => {
    const sub = mqttService.notification$.subscribe((notification) => {
      if (notification.type === NOTIFICATION_TYPES.NOT_USER_ACTION_REQUIRED) {
        const currentLastNotification = lastNotificationRef.current;
        const currentUserInfo = userInfoRef.current;
        const notifKey =
          notification.seq ||
          notification.message ||
          JSON.stringify(notification);

        if (
          !currentLastNotification.includes(notifKey) &&
          currentUserInfo?.notifications === true
        ) {
          setLastNotification((prev) =>
            prev.length >= 5
              ? [...prev.slice(1), notifKey]
              : [...prev, notifKey],
          );
          notifyUser(notification);
        }
      }
    });

    return () => {
      sub.unsubscribe();
    };
  }, []);

  return <div />;
};

export default GlobalNotificationDisplayer;
